import { forwardRef, useContext, useImperativeHandle, useRef, useState } from 'react';
import { PartialPick } from 'shared';

import { media } from 'context';
import IconButton from 'ds/IconButton';
import Layout from 'ds/Layout';
import { Props as TextInputProps } from 'ds/inputs/TextInput';
import { InputImageIconProps } from 'ds/inputs/types';

import MultiSelectBottomSheet from './MultiSelectBottomSheet';
import Typeahead from './Typeahead';

export type Props = Pick<
  TextInputProps,
  'label' | 'bigLabel' | 'size' | 'icon' | 'disabled' | 'onClear' | 'placeholder'
> &
  PartialPick<TextInputProps, 'onChange'> &
  Pick<InputImageIconProps, 'image'> & {
    noMatchesItem?: JSX.Element;
    values: string[];
    selections: string[];
    onSelect: (value: string) => void;
    onDelete: (value: string) => void;
    showResultsWhenEmpty?: boolean;
    maxItems?: number;
    minItems?: number;
    highlightMatch?: boolean;
    required?: boolean;
    hideLabel?: boolean;
    error?: string;
    allowNewEntry?: boolean;
    onCreateNew?: VoidFunction;
    forceCapitalize?: boolean;
  };

export type MultiSelectHandle = {
  /** Typed value not yet added as a selection; null if empty, duplicate, or new entries disabled. */
  getPendingQuery: () => string | null;
  /** Adds pending query to selections and clears the input, when applicable. */
  commitPendingQuery: () => void;
};

const MultiSelect = forwardRef<MultiSelectHandle, Props>(function MultiSelect(
  {
    size = 'sm',
    icon = 'search',
    label,
    onSelect,
    onDelete,
    values,
    selections,
    showResultsWhenEmpty = false,
    allowNewEntry = true,
    hideLabel = false,
    maxItems = 8,
    minItems = 0,
    highlightMatch,
    disabled,
    placeholder,
    required = false,
    error,
    forceCapitalize = false,
    onChange
  },
  ref
) {
  const [query, setQuery] = useState<string>('');
  const onSelectRef = useRef(onSelect);
  onSelectRef.current = onSelect;
  const { xs } = useContext(media);
  const [modalIsVisible, setModalIsVisible] = useState<boolean>(false);

  const remainingValues = values.filter(value => !selections.includes(value));
  const results = remainingValues
    .filter(value => value.toString().toLowerCase().includes(query.toLowerCase()))
    .map(value => ({ value, label: value }));
  const handleChange = (value: string) => {
    setQuery(forceCapitalize && value.length ? value.charAt(0).toUpperCase() + value.slice(1) : value);
  };
  const handleSelect = (value: string) => {
    if (selections?.includes(value)) return;

    onSelect(value);
    setQuery('');
  };

  const selectionKeys = selections.join('\n');

  useImperativeHandle(
    ref,
    () => ({
      getPendingQuery: () => {
        const trimmed = query.trim();
        if (!allowNewEntry || trimmed.length === 0) {
          return null;
        }
        const currentSelections = selectionKeys.length > 0 ? selectionKeys.split('\n') : [];
        const isDuplicate = currentSelections.some(s => s.toLowerCase() === trimmed.toLowerCase());
        return isDuplicate ? null : trimmed;
      },
      commitPendingQuery: () => {
        const trimmed = query.trim();
        if (!allowNewEntry || trimmed.length === 0) {
          return;
        }
        const currentSelections = selectionKeys.length > 0 ? selectionKeys.split('\n') : [];
        if (currentSelections.some(s => s.toLowerCase() === trimmed.toLowerCase())) {
          return;
        }
        onSelectRef.current(trimmed);
        setQuery('');
      }
    }),
    [query, allowNewEntry, selectionKeys]
  );

  return (
    <>
      {xs && (
        <MultiSelectBottomSheet
          onClose={() => setModalIsVisible(false)}
          isVisible={modalIsVisible}
          results={results}
          label={label}
          handleChange={handleChange}
          handleSelect={handleSelect}
          highlightMatch={highlightMatch}
          selections={selections}
          onDelete={onDelete}
          query={query}
          minItems={minItems}
        />
      )}
      <Layout marginTop={12}>
        <Typeahead
          size={size}
          label={label}
          icon={icon}
          onSelect={({ value }) => handleSelect(value)}
          results={results}
          onChange={params => {
            handleChange(params.value);
            onChange && onChange(params);
          }}
          query={query}
          allowNewEntry={allowNewEntry}
          closeResultsOnSelect={false}
          hideLabel={hideLabel}
          showResultsWhenEmpty={showResultsWhenEmpty}
          maxItems={maxItems}
          highlightMatch={highlightMatch}
          disabled={disabled}
          placeholder={placeholder}
          required={required}
          error={error}
          onClear={() => setQuery('')}
          onFocus={() => setModalIsVisible(true)}
        />
      </Layout>
      <Layout paddingTop={4}>
        <Layout wrap>
          {selections?.map((name, index) => {
            return (
              <Layout
                key={name}
                paddingX={6}
                paddingY={2}
                borderRadius={4}
                align="center"
                color="gray-25"
                marginTop={12}
                marginLeft={index === 0 ? undefined : 4}
              >
                <p style={{ fontSize: 10, lineHeight: '16px' }}>{name}</p>
                <Layout marginLeft={6}>
                  <IconButton
                    name="close"
                    size="xs"
                    type="noBackground"
                    onClick={() => onDelete(name)}
                    disabled={selections.length <= minItems}
                  />
                </Layout>
              </Layout>
            );
          })}
        </Layout>
      </Layout>
    </>
  );
});

export default MultiSelect;
